/**
 * @file sessionTrend.ts
 * @brief Client-side session bucketing for Company Admin dashboard charts.
 *
 * @author DES
 * @date 18-06-2026
 */

export type SessionTrendBucket = {
  period: string;
  session_count: number;
  total_cost_cents: number;
};

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDayKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Build daily buckets (inclusive) and fill missing days with zero. */
export function buildDailySessionTrend(
  sessions: ReadonlyArray<{
    started_at?: string | null;
    total_cents?: number | null;
  }>,
  startDate: string,
  endDate: string,
): SessionTrendBucket[] {
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const buckets = new Map<string, SessionTrendBucket>();

  for (
    let cursor = new Date(start);
    cursor <= end;
    cursor.setDate(cursor.getDate() + 1)
  ) {
    const key = dayKey(cursor);
    buckets.set(key, {
      period: parseDayKey(key).toISOString(),
      session_count: 0,
      total_cost_cents: 0,
    });
  }

  for (const row of sessions) {
    if (!row.started_at) continue;
    const started = new Date(row.started_at);
    if (Number.isNaN(started.getTime())) continue;
    const key = dayKey(started);
    const bucket = buckets.get(key);
    if (!bucket) continue;
    bucket.session_count += 1;
    bucket.total_cost_cents += Number(row.total_cents ?? 0);
  }

  return Array.from(buckets.values()).sort(
    (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime(),
  );
}
